import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, DollarSign, TrendingUp, GraduationCap, Trophy, Users, Star,
  BadgeCheck, Compass, Download, ArrowRight, Award,
} from "lucide-react";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicResults() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/results/${id}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [id]);

  if (error) {
    return (
      <div className="min-h-screen bg-brand-bg flex items-center justify-center px-4">
        <div className="nb-card p-8 bg-brand-yellow text-center max-w-sm">
          <h2 className="font-heading text-2xl font-bold mb-3">Results not found</h2>
          <Link to="/" className="nb-btn bg-brand-primary text-white inline-flex">Go home</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen bg-brand-bg flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-brand-primary" /></div>;
  }

  const [top, ...rest] = data.matches;

  return (
    <div className="min-h-screen bg-brand-bg">
      <header className="border-b-2 border-black bg-brand-bg">
        <div className="max-w-5xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="bg-brand-primary border-2 border-black rounded-lg p-1.5 shadow-nb-xs">
              <Compass className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading text-xl font-bold text-black">TalentSwipe<span className="text-brand-primary">.us</span></span>
          </Link>
          <Link to="/" data-testid="public-cta" className="nb-btn bg-brand-primary text-white text-sm flex items-center gap-2 py-2">
            Take the quiz <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
        <span className="nb-badge bg-brand-yellow mb-2"><Trophy className="w-4 h-4" /> Shared results</span>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-black mb-1">{data.owner_name}'s career matches</h1>
        <p className="font-semibold text-slate-600 mb-6">Discovered by swiping through activities on TalentSwipe.us.</p>

        {data.veteran && (data.veteran.code || data.veteran.rating_title) && (
          <div data-testid="veteran-thankyou" className="nb-card p-4 bg-brand-secondary mb-6 flex items-center gap-3">
            <div className="bg-white border-2 border-black rounded-lg p-2 shadow-nb-xs">
              <Award className="w-5 h-5 text-black" />
            </div>
            <p className="font-heading text-lg font-bold text-black leading-tight">Thank you for your service. 🇺🇸</p>
          </div>
        )}

        <button
          data-testid="public-download-pdf"
          onClick={() => {
            const a = document.createElement("a");
            a.href = `${BACKEND}/api/public/results/${id}/pdf`;
            a.download = "career-report.pdf";
            document.body.appendChild(a);
            a.click();
            a.remove();
          }}
          className="nb-btn bg-brand-yellow flex items-center gap-2 mb-6"
        >
          <Download className="w-4 h-4" /> Download PDF
        </button>

        {top && (
          <div className="nb-card p-6 md:p-8 bg-brand-secondary mb-6">
            <span className="nb-badge bg-brand-yellow mb-2">Top Match · {top.match_score}%</span>
            {top.interest_area && (
              <span className="nb-badge ml-2 mb-2" style={{ backgroundColor: top.interest_area.color }}>
                Mostly {top.interest_area.title}
              </span>
            )}
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-black">{top.title}</h2>
            <p className="font-semibold text-black/80 mt-2 max-w-2xl">{top.description}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              <span className="nb-badge bg-white"><DollarSign className="w-4 h-4" /> ${top.salary.toLocaleString()}/yr</span>
              <span className="nb-badge bg-white"><TrendingUp className="w-4 h-4" /> {top.outlook}</span>
              <span className="nb-badge bg-white"><GraduationCap className="w-4 h-4" /> {top.education}</span>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {rest.slice(0, 6).map((occ, i) => (
            <motion.div
              key={occ.onet_code}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="nb-card p-5"
            >
              <span className="font-heading text-3xl font-bold text-brand-primary">{occ.match_score}%</span>
              <h3 className="font-heading text-lg font-bold text-black mb-1">{occ.title}</h3>
              {occ.interest_area && (
                <span className="nb-badge text-xs mb-2" style={{ backgroundColor: occ.interest_area.color }}>
                  {occ.interest_area.title}
                </span>
              )}
              <p className="text-sm font-semibold text-slate-600">{occ.description}</p>
            </motion.div>
          ))}
        </div>

        {data.social_proof?.length > 0 && (
          <section className="mb-10">
            <span className="nb-badge bg-brand-purple mb-3"><Users className="w-4 h-4" /> People like {data.owner_name}</span>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {data.social_proof.map((p, i) => (
                <div key={i} className="nb-card p-5 text-center">
                  <img src={p.avatar} alt={p.name} className="w-16 h-16 rounded-full border-2 border-black object-cover mx-auto mb-3 shadow-nb-xs" />
                  <p className="font-heading font-bold text-black flex items-center justify-center gap-1">
                    {p.name}{p.is_real && <BadgeCheck className="w-4 h-4 text-brand-secondary" />}
                  </p>
                  <p className="text-sm font-bold text-brand-primary mb-2">{p.profession}</p>
                  <div className="flex justify-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= p.happiness ? "fill-brand-yellow text-black" : "text-slate-300"}`} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="nb-card p-8 bg-brand-primary text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-3">Curious what fits you?</h2>
          <Link to="/" data-testid="public-bottom-cta" className="nb-btn bg-white inline-flex items-center gap-2">
            Start swiping <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
